// Plans (PR 1a): pure texts shared by the admin pages and Settings → Plan, tested in
// test/plans.test.ts. The server words the order summary itself (plan-rules.ts); these
// are the small labels around it, and reading what an admin types into price fields.

import type { PlanCategory, Terms } from './api';

/** Thousands separators ("1,200"); numbers only, never times (those go through time.ts). */
const GROUPED = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/** 1165 → "$11.65", 123456 → "$1,234.56". */
export function money(cents: number): string {
  const neg = cents < 0;
  const c = Math.abs(Math.trunc(cents));
  return `${neg ? '−' : ''}$${GROUPED.format(Math.floor(c / 100))}.${String(c % 100).padStart(2, '0')}`;
}

/** A per-unit rate: 0.05 → "$0.05", 0.0085 → "$0.0085". */
export function rateText(dollars: number): string {
  let s = dollars.toFixed(4).replace(/0+$/, '');
  if (/\.\d$/.test(s)) s += '0';
  if (s.endsWith('.')) s += '00';
  return `$${s}`;
}

export const count = (n: number) => GROUPED.format(n);

export const CATEGORY_TITLES: Record<PlanCategory, string> = { individual: 'Individual', team: 'Team', enterprise: 'Enterprise' };

/** "$9.99/month", Team plans "$4.99/user/month". */
export function priceText(t: { priceCents: number; category: PlanCategory }): string {
  return `${money(t.priceCents)}/${t.category === 'team' ? 'user/' : ''}month`;
}

/** "300 minutes · 250 texts" / "Pay per use" */
export function allowanceText(t: { minutes: number; texts: number }): string {
  const parts = [...(t.minutes > 0 ? [`${count(t.minutes)} minutes`] : []), ...(t.texts > 0 ? [`${count(t.texts)} texts`] : [])];
  return parts.length ? parts.join(' · ') : 'Pay per use';
}

/** "Extra minute $0.05 · extra number $3.00/month" (what costs more than the plan). */
export function extrasText(t: Pick<Terms, 'overagePerMin' | 'numberCents' | 'firstNumberIncluded'>): string {
  return `Extra minute ${rateText(t.overagePerMin)} · ${t.firstNumberIncluded ? 'extra number' : 'every number'} ${money(t.numberCents)}/month`;
}

const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** 'YYYY-MM-DD' (a billing day) → "Dec 1" ("Jan 1, 2027" when it isn't this year). */
export function billingDayText(day: string, now: Date = new Date()): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  const y = Number(m[1]);
  // a billing day is a calendar date (no time): "this year" by the UTC calendar is close enough for a label
  return `${SHORT[Number(m[2]) - 1]} ${Number(m[3])}${y !== now.getUTCFullYear() ? `, ${y}` : ''}`;
}

// ── What an admin types ──

/** "9.99", "$9.99", "1,234.5", "10" → cents; null when it isn't a price with at most 2 decimals. */
export function centsFrom(input: string): number | null {
  const s = input.trim().replace(/^\$/, '').replace(/,/g, '');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  return Number(whole) * 100 + Number(frac.padEnd(2, '0'));
}
/** cents → "9.99" for a price field */
export const centsField = (cents: number) => (cents / 100).toFixed(2);

/** "0.05", "$0.0085" → dollars; null when it isn't a rate with at most 4 decimals. */
export function rateFrom(input: string): number | null {
  const s = input.trim().replace(/^\$/, '');
  if (!/^\d{1,3}(\.\d{1,4})?$/.test(s)) return null;
  const v = Number(s);
  return v <= 100 ? v : null;
}
/** dollars → "0.05" / "0.0085" for a rate field */
export const rateField = (v: number) => rateText(v).slice(1);

/** A whole number field ("1,200" → 1200); null when it isn't one. */
export function wholeFrom(input: string, max = 1_000_000): number | null {
  const s = input.trim().replace(/,/g, '');
  if (!/^\d{1,7}$/.test(s)) return null;
  const n = Number(s);
  return n <= max ? n : null;
}

/** PR 3a: an amount an admin typed ("12.50", "$12.50", "1,200") → cents (at most 2 decimals, > 0), else null. Same rule as the server. */
export function amountCents(raw: string): number | null {
  const s = raw.replace(/[$,\s]/g, '');
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(s)) return null;
  const [whole, frac = ''] = s.split('.');
  const c = Number(whole) * 100 + Number(frac.padEnd(2, '0'));
  return c > 0 ? c : null;
}
